import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AutomataProvider } from './contexts/AutomataContext';
import { Toaster } from './components/ui/sonner';
import LandingPage from './pages/LandingPage';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RecoverAccountPage from './pages/RecoverAccountPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ImpersonationBanner from './components/ImpersonationBanner';
import { BugReportButton } from './components/BugReportButton';
import './App.css';
import './utils/consoleCapture';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Skip if user data passed from AuthCallback
  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Show bug report button for logged-in users
const AuthenticatedBugReport = () => {
  const { user } = useAuth();
  const location = useLocation();
  // Show on /editor (guest mode) and all authenticated pages
  if (!user && location.pathname !== '/editor') return null;
  return <BugReportButton />;
};

// App Router - handles OAuth callback detection
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (sync check before rendering routes)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/editor"
        element={
          <AutomataProvider>
            <Dashboard />
          </AutomataProvider>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AutomataProvider>
              <Dashboard />
            </AutomataProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/recover-account" element={<RecoverAccountPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <div className="h-screen flex flex-col">
            <ImpersonationBanner />
            <div className="flex-1 min-h-0 overflow-y-auto relative">
              <AppRouter />
            </div>
          </div>
          <AuthenticatedBugReport />
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
